import JSZip from "jszip";
import { defer, forkJoin, from, Observable, of, zip } from "rxjs";
import { every, map, mergeMap } from "rxjs/operators";

import { HttpErrorResponse, HttpEvent, HttpResponse, HttpStatusCode } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { FormatDate } from "../../_objects/format-date";
import { DataProperty, FILE_SYSTEM, HttpErrorMessage, HttpStatusText } from "../../_offline/electron.constants";
import { IndividualReportTemplateSchema } from "../../_offline/models/individual-report-template.schema";
import { PreviewImageDetail } from "../../_offline/models/report-preview/preview-image-detail";
import {
  EditableTextList,
  ImagePath,
  IndividualReportTemplate,
  ReportTemplate,
  ReportTemplateImageList,
  ReportTemplateListItem,
  ReportTemplateNew,
  ReportTemplateUpdate,
  ReportTitleFormat,
  ResponseIdList,
  TemplateDefinition,
  TemplateText,
  UpdateIdList,
  UpdateResultList
} from "../../_offline/models/report-preview/report-template";
import { TableData } from "../../_offline/models/report-preview/TableData";
import { ViewDataModel } from "../../_offline/models/report-preview/view-data-model";
import { UtilsService } from "../_utils.service";
import { IndividualReportDbService } from "../database/individual-report-db.service";
import { IndividualReportTemplateDbService } from "../database/individual-report-template-db.service";
import { FileSystemAccessService } from "../file-system-access.service";
import { ReportTemplateContractService } from "../interfaces/report-template-contract.service";
import { LoginUserOfflineService } from "./login-user-offline.service";
import { FeatureFlagOfflineService } from "../feature-flag/feature-flag.offline.service";
import { LiveAssetsService } from "@rvi/asset-modules";

@Injectable({
  providedIn: "root"
})
export class ReportTemplateOfflineService implements ReportTemplateContractService {
  constructor(
    private fileSystemAccessService: FileSystemAccessService,
    private loginUserOfflineService: LoginUserOfflineService,
    private reportTemplateDbService: IndividualReportTemplateDbService,
    private reportDbService: IndividualReportDbService,
    private utilsService: UtilsService,
    private featureFlagService: FeatureFlagOfflineService,
    private liveAssetsService: LiveAssetsService
  ) {}

  public inspectionSummary: TableData[][];
  public previewImages: PreviewImageDetail[];

  private get isAssetModuleEnabled() {
    return this.featureFlagService.isFlagEnabled("asset_module")();
  }

  getReportTemplates(subjectModelId?: string, enabledOnly?: boolean): Observable<ReportTemplateListItem[]> {
    const filterId$ =
      subjectModelId && this.featureFlagService.isFlagEnabled("asset_module")()
        ? this.liveAssetsService.getLiveAssetDetails(subjectModelId).pipe(map((liveAsset) => liveAsset.assetType?.id ?? subjectModelId))
        : of(subjectModelId);

    return filterId$.pipe(
      mergeMap((resolvedId) => this.reportTemplateDbService.getAll(resolvedId, enabledOnly)),
      map((reportTemplates) => reportTemplates.map((reportTemplate) => this.convertToReportTemplateList(reportTemplate)))
    );
  }

  getReportTemplate(reportTemplateId: string): Observable<ReportTemplate> {
    return this.reportTemplateDbService.get(reportTemplateId).pipe(
      map((reportTemplate) => {
        const result = new ReportTemplate();
        result.id = reportTemplate.id;
        result.name = reportTemplate.name;
        result.isValid = reportTemplate.isValid;
        result.lastUpdateDate = reportTemplate.lastUpdateDate;
        result.reportTitleInputMode = reportTemplate.reportTitleInputMode;
        result.reportTitleFormat = JSON.parse(reportTemplate.reportTitleFormat) as ReportTitleFormat[];
        result.subjectModelIds = JSON.parse(reportTemplate.subjectModelIds) as string[];
        result.schemaVersion = reportTemplate.schemaVersion;
        result.template = JSON.parse(reportTemplate.template) as TemplateDefinition;
        result.createdBy = reportTemplate.createdBy;
        result.createDate = reportTemplate.createDate;
        result.lastUpdatedBy = reportTemplate.lastUpdatedBy;
        result.lastUpdateDate = reportTemplate.lastUpdateDate;
        return result;
      })
    );
  }

  getEditableTexts(reportTemplateId: string, reportId: string): Observable<EditableTextList[]> {
    const dynamicEditable = "dynamic-editable";
    return zip(this.reportTemplateDbService.get(reportTemplateId), this.reportDbService.getAllVersions(reportId)).pipe(
      map((values) => {
        const reportTemplate = values[0];
        const reports = values[1];

        const editableTextList: EditableTextList[] = [];
        const template = JSON.parse(reportTemplate.template) as TemplateDefinition;
        const headerTexts = template.header.texts;
        headerTexts.forEach((text) => {
          if (text.type === dynamicEditable) {
            const editableText = this.genEditableText(text);
            editableTextList.push(editableText);
          }
        });

        const footerTexts = template.footer.texts;
        footerTexts.forEach((text) => {
          if (text.type === dynamicEditable) {
            const editableText = this.genEditableText(text);
            editableTextList.push(editableText);
          }
        });

        const bodyTexts = template.bodies.map((body) => body.texts);

        bodyTexts.forEach((texts) => {
          texts.forEach((text) => {
            if (text.type === dynamicEditable) {
              const editableText = this.genEditableText(text);
              editableTextList.push(editableText);
            }
          });
        });

        const copyItem = [];
        const viewData = new ViewDataModel();
        const tableColumns = template.bodies.map((body) => body.inspectionSummary?.table?.columns);
        viewData.isAllDataMode =
          template.bodies.find((body) => body.inspectionSummary !== null)?.inspectionSummary.table.dataMode === DataProperty.All;
        this.inspectionSummary?.forEach((summary) => {
          summary.forEach((tableData) => {
            const image = this.previewImages.find((_) => tableData.images.some((image) => image.id === _.id));
            viewData.summaryData.push({
              tableData: tableData,
              imageId: image ? (image.isDummy ? "" : image?.id) : "",
              fileName: image?.fileName ?? "NoImage"
            });
          });
        });
        tableColumns?.forEach((columns) => {
          columns?.forEach((column) => {
            column?.textsInfo?.forEach((text) => {
              if (text?.type === dynamicEditable) {
                if (text?.isTableText) {
                  viewData.summaryData.forEach((item) => {
                    const label = text.label + "_" + item.tableData.names.join("_") + (viewData.isAllDataMode ? "_" + item.fileName : "");
                    const editableId = text.editableId + "_" + item.tableData.id + (viewData.isAllDataMode ? "_" + item.imageId : "");
                    copyItem.push({
                      editableId: editableId,
                      type: text.type,
                      inputType: text.inputType,
                      label: label,
                      validation: text.validation,
                      options: text.options,
                      multiSelectDelimiter: text.multiSelectDelimiter,
                      radioLabel: text.radioLabel,
                      value: text.value,
                      radioType: text.radioType,
                      inputGroup: text.inputGroup,
                      text: text.text,
                      isTableText: false
                    } as TemplateText);
                  });
                  copyItem.forEach((item) => {
                    const editableText = this.genEditableText(item);
                    editableTextList.push(editableText);
                  });
                } else {
                  const editableText = this.genEditableText(text);
                  editableTextList.push(editableText);
                }
              }
            });
          });
        });

        const maxVersion = Math.max(...reports.map((r) => r.version));
        const target = reports.find((report) => report.version === maxVersion);
        const reportEditableTexts = JSON.parse(target.editableTexts);

        Object.keys(reportEditableTexts).forEach((key) => {
          editableTextList.some((text) => {
            if (text.editableId === key) {
              text.text = editableTextList[key];
              return true;
            }
            return false;
          });
        });

        return editableTextList;
      })
    );
  }

  updateReportTemplate(reportTemplateUpdate: ReportTemplateUpdate): Observable<ReportTemplate> {
    return of();
  }

  addReportTemplate(reportTemplateNew: ReportTemplateNew): Observable<ReportTemplate> {
    return of();
  }

  public exportReportTemplate(reportTemplateId: string): Observable<Blob> {
    const templateInfo = this.getReportTemplate(reportTemplateId);

    const imagesFiles = this.getReportTemplateImages(reportTemplateId).pipe(
      mergeMap((images) => Promise.all(images.map((image) => this.fileSystemAccessService.getFile(image.originalUrl, image.fileName, "images"))))
    );

    return forkJoin({ templateInfo, imagesFiles }).pipe(
      mergeMap(({ templateInfo, imagesFiles }) => this.createReportTemplateZip(templateInfo, imagesFiles))
    );
  }

  private createReportTemplateZip(reportTemplate: ReportTemplate, images: File[]) {
    const zip = new JSZip();

    const json = JSON.stringify(reportTemplate, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    zip.file(`${reportTemplate.name}.json`, blob);

    zip.folder("images");
    images.forEach((file) => zip.file(`images/${file.name}`, file));

    return zip.generateAsync({ type: "blob" });
  }

  importReportTemplate(file: File, reportTemplate?: ReportTemplateListItem): Observable<HttpEvent<{}>> {
    let jsonString: string = "";
    const imageFileNameBlobs: Map<string, Blob> = new Map<string, Blob>();
    const individualReportTemplate: IndividualReportTemplate = new IndividualReportTemplate();

    return defer(() => JSZip.loadAsync(file)).pipe(
      mergeMap((x) => {
        // ZIP から取り出す
        const compressedFiles: Array<Observable<any>> = new Array<Observable<any>>();
        x.forEach((relativePath: string, file: JSZip.JSZipObject) => {
          if (file.dir) {
            // ディレクトリーはスキップする。
            return;
          } else if (file.name.endsWith(".json")) {
            compressedFiles.push(
              defer(async () => {
                return await file.async("string").then((x) => {
                  // JSON の取得。
                  jsonString = x;
                  return x;
                });
              })
            );
          } else {
            compressedFiles.push(
              defer(async () => {
                return await file.async("blob").then((x) => {
                  // 画像データの取得。
                  imageFileNameBlobs.set(this.utilsService.getFileName(file.name), x);
                  return x;
                });
              })
            );
          }
        });

        return forkJoin(compressedFiles);
      }),
      mergeMap(() => {
        // JSON 情報を保存
        individualReportTemplate.setValueFromJsonObject(
          JSON.parse(jsonString),
          this.loginUserOfflineService.loginUser.id,
          this.loginUserOfflineService.tenantId
        );

        const imagePaths: Array<ImagePath> = new Array<ImagePath>();
        for (const fileName of imageFileNameBlobs.keys()) {
          const imagePath: ImagePath = new ImagePath();
          imagePath.id = fileName;
          imagePath.src = `${FILE_SYSTEM.PATH.REPORT_TEMPLATE}/${individualReportTemplate.id}/images/${fileName}`;

          imagePaths.push(imagePath);
        }
        individualReportTemplate.imagePathList = imagePaths;

        const individualReportTemplateSchema: IndividualReportTemplateSchema = new IndividualReportTemplateSchema(
          individualReportTemplate,
          this.loginUserOfflineService.tenantId
        );
        return this.reportTemplateDbService.put(individualReportTemplateSchema);
      }),
      mergeMap(() => {
        // 画像ファイルを枚数分保存
        return from(imageFileNameBlobs.entries()).pipe(
          mergeMap((x) => {
            const fileName: string = x[0];
            const blob: Blob = x[1];
            const file: File = new File([blob], fileName, { type: this.utilsService.getMimeType(fileName) });
            return defer(() =>
              this.fileSystemAccessService.saveFile(
                file,
                `${FILE_SYSTEM.PATH.REPORT_TEMPLATE}/${individualReportTemplate.id}/images/${fileName}`.replace(/\//g, "\\")
              )
            );
          }),
          every((x) => x)
        );
      }),
      // TODO: IPLEX Mode チェック処理
      mergeMap(() => of(new HttpResponse()))
    );
  }

  getModelsForTemplates(reportTemplateId?: string, subjectModelId?: string): Observable<ResponseIdList[]> {
    return of();
  }

  updateModelsForTemplates(templateId: string, updateModelIdList: UpdateIdList): Observable<UpdateResultList> {
    return this.reportTemplateDbService.get(templateId).pipe(
      mergeMap((x) => {
        x.assetTypeIds = JSON.stringify(updateModelIdList.subjectModelIds);
        x.subjectModelIds = JSON.stringify(updateModelIdList.subjectModelIds);
        x.lastUpdateDate = new FormatDate().toString();
        x.lastUpdatedBy = this.loginUserOfflineService.loginUser.id;

        return this.reportTemplateDbService.put(x);
      }),
      mergeMap((x) => {
        const result: UpdateResultList = new UpdateResultList();

        const ids: string[] = IndividualReportTemplateSchema.effectiveModelIds(x, this.isAssetModuleEnabled);
        const responseIdLists: Array<ResponseIdList> = ids.map((id) => {
          const responseIdList: ResponseIdList = new ResponseIdList();
          responseIdList.reportTemplateId = templateId;
          responseIdList.subjectModelId = id;
          return responseIdList;
        });

        result.updateResult = responseIdLists;

        return of(result);
      })
    );
  }

  getReportTemplateImages(templateId: string): Observable<ReportTemplateImageList[]> {
    return this.reportTemplateDbService.get(templateId).pipe(
      map((reportTemplate) => {
        const imageList = JSON.parse(reportTemplate.imagePathList) as ImagePath[];
        return imageList.map((image) => {
          const result = new ReportTemplateImageList();
          result.fileName = image.id;
          result.originalUrl = image.src;
          result.thumbUrl = image.src;
          return result;
        });
      })
    );
  }

  updateReportTemplateImage(templateId: string, file: File): Observable<HttpEvent<{}>> {
    return of();
  }

  deleteReportTemplateImage(templateId: string, fileName: string): Observable<any> {
    return of();
  }

  deleteReportTemplate(templateId: string): Observable<any> {
    return this.reportDbService.isInspectionReportUsingTemplateIdExists(templateId).pipe(
      mergeMap((x) => {
        if (x) {
          // レポートで使用中の場合は削除不可。
          throw new HttpErrorResponse({
            error: `${HttpErrorMessage.Conflict.ReportTemplateAlreadyUsed}${templateId}`,
            status: HttpStatusCode.Conflict,
            statusText: HttpStatusText.Conflict
          });
        }

        // 削除対象画像のディレクトリーを取得。
        return defer(() => this.fileSystemAccessService.getFileFullPath(`${FILE_SYSTEM.PATH.REPORT_TEMPLATE}/${templateId}`));
      }),
      mergeMap((directoryPath) => this.reportTemplateDbService.delete(templateId, this.loginUserOfflineService.loginUser.id, directoryPath)),
      mergeMap((x) => {
        if (!x) {
          // DB のトランザクションに失敗した場合はエラーを返す。
          throw new HttpErrorResponse({
            error: `${HttpErrorMessage.InternalServerError.SQLError}`,
            status: HttpStatusCode.InternalServerError,
            statusText: HttpStatusText.InternalServerError
          });
        }

        return of(new HttpResponse());
      })
    );
  }

  genEditableText(text: TemplateText) {
    const editableText = new EditableTextList();

    editableText.editableId = text.editableId;
    editableText.inputType = text.inputType;
    editableText.label = text.label;
    editableText.radioLabel = text.radioLabel;
    editableText.value = text.value;
    editableText.text = text.text;
    editableText.validation = text.validation;
    editableText.options = text.options;
    editableText.inputGroup = text.inputGroup;

    return editableText;
  }

  /**
   * ReportTemplateList に変換する。
   *
   * @param reportTemplateSchema IndividualReportTemplateSchema
   * @returns ReportTemplateListItem
   */
  private convertToReportTemplateList(reportTemplateSchema: IndividualReportTemplateSchema): ReportTemplateListItem {
    const result = new ReportTemplateListItem();

    result.id = reportTemplateSchema.id;
    result.name = reportTemplateSchema.name;
    result.isValid = reportTemplateSchema.isValid;
    result.lastUpdateDate = reportTemplateSchema.lastUpdateDate;
    result.reportTitleInputMode = reportTemplateSchema.reportTitleInputMode;
    result.reportTitleFormat = !reportTemplateSchema.reportTitleFormat
      ? new Array<ReportTitleFormat>()
      : (JSON.parse(reportTemplateSchema.reportTitleFormat) as Array<ReportTitleFormat>);
    result.subjectModelIds = IndividualReportTemplateSchema.effectiveModelIds(reportTemplateSchema, this.isAssetModuleEnabled)
    result.isOfflineData = reportTemplateSchema.isOfflineData;
    result.pdfPreviewPath = reportTemplateSchema.imagePathList ? this.getPdfPreview(reportTemplateSchema.imagePathList) : undefined;

    return result;
  }

  private getPdfPreview(imagePathList: string) {
    const imagePaths = JSON.parse(imagePathList) as Array<ImagePath>;
    return imagePaths.find((path) => path.src.includes("/preview"))?.src;
  }

  /**
   * ReportTemplateList を取得する。
   *
   * @param reportTemplateId レポートテンプレート ID。
   * @returns ReportTemplateList。
   */
  public getReportTemplateListById(reportTemplateId: string): Observable<ReportTemplateListItem> {
    return this.reportTemplateDbService
      .get(reportTemplateId)
      .pipe(map((reportTemplateSchema) => this.convertToReportTemplateList(reportTemplateSchema)));
  }
}
